// TemplateWizard.tsx
import React, { useState } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"
import { useTemplates } from "features/QuestionnaireEditor/api/useTemplates"

import TemplateList from "./TemplateWizardSteps/TemplateList.component"
import SelectedTemplateForm from "./TemplateWizardSteps/SelectTemplateForm.component"
import PreviewTemplate from "./TemplateWizardSteps/PreviewTemplate.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

import { useProjectStore } from "stores/projectStore"
import TemplateFiles from "./TemplateWizardSteps/TemplateFiles.component"
import {
  TemplateFileListResult,
  TemplateItem,
  TemplateParseResult,
  SelectedFile,
  UserInputs as TemplateUserInputs,
  TemplateOptions,
} from "types/template.types"

// Define component-specific interfaces
interface UserInput {
  label: string
  value: string
}

interface UserInputsState {
  "template-label": string
  inputs: UserInput[]
}

// Define the props interface for TemplateWizard
interface TemplateWizardProps {
  /** Function to toggle the visibility of the modal */
  setTemplateModalVisible: (visible: boolean) => void
  /** The list of available template files */
  templateFiles: TemplateFileListResult
  /** The editor instance used to insert the final template content */
  editor: TiptapEditor | null
  /** Indicates if the template should be used in the master configuration */
  inMaster: boolean
  /** Indicates if the questionnaire is empty */
  qnrEmpty: boolean
  /** Function to set the current step in the wizard */
  setCurrentStep: (step: number) => void
  /** The current step number in the wizard */
  currentStep: number
}

/**
 * TemplateWizard component provides a step-by-step interface for selecting,
 * configuring, and previewing templates in the Data(Q) system.
 *
 * @param props - The component props
 * @returns The rendered TemplateWizard component
 */
const TemplateWizard: React.FC<TemplateWizardProps> = (props) => {
  const {
    setTemplateModalVisible,
    templateFiles,
    editor,
    inMaster,
    qnrEmpty,
    setCurrentStep,
    currentStep,
  } = props

  const project = useProjectStore((state) => state.project)
  const {
    listTemplatesFromFile,
    listingTemplatesFromFile,
    parseSelectedTemplate,
    parsingSelectedTemplate,
    mergeTemplateWithUserInputs,
  } = useTemplates()

  // Typed state variables
  const [templates, setTemplates] = useState<TemplateItem[]>([])
  const [parsedTemplate, setParsedTemplate] = useState<TemplateParseResult>({
    information: {
      "template-label": "",
      description: "",
      exceptions: [],
    },
    sections: [],
  })
  const [selectedTemplateFile, setSelectedTemplateFile] =
    useState<SelectedFile>({
      template: "",
      type: "",
    })
  const [userInputs, setUserInputs] = useState<UserInputsState>({
    "template-label": "",
    inputs: [],
  })
  const [templateOutput, setTemplateOutput] = useState<string>("")

  /**
   * Hides the modal and resets the state.
   *
   * @function hideModal
   */
  const hideModal = (): void => {
    // Reset all state to initial values
    setCurrentStep(0)
    setTemplates([])
    setParsedTemplate({
      information: {
        "template-label": "",
        description: "",
        exceptions: [],
      },
      sections: [],
    })
    setSelectedTemplateFile({
      template: "",
      type: "",
    })
    setUserInputs({
      "template-label": "",
      inputs: [],
    })
    setTemplateOutput("")
    setTemplateModalVisible(false)
  }

  /**
   * Handles the selection of a template file and lists the available templates.
   *
   * @async
   * @function handleTemplateFilesItemClick
   * @param templateFile - The selected template file
   * @returns Promise that resolves when templates are loaded
   */
  const handleTemplateFilesItemClick = async (
    templateFile: SelectedFile
  ): Promise<void> => {
    setCurrentStep(1)
    try {
      setSelectedTemplateFile(templateFile)
      const templateRes = await listTemplatesFromFile(templateFile)
      setTemplates(templateRes.data.templateList.result)
    } catch (error) {
      console.error("Error parsing template:", error)
    }
  }

  /**
   * Handles the selection of a specific template and parses it.
   *
   * @async
   * @function handleTemplateItemClick
   * @param text - The selected template's label or identifier
   * @returns Promise that resolves when template is parsed
   */
  const handleTemplateItemClick = async (text: string): Promise<void> => {
    setCurrentStep(2)
    const parseOptions: TemplateOptions = {
      inMaster,
      qnrEmpty,
    }
    try {
      const parseResponse = await parseSelectedTemplate(
        project.id,
        selectedTemplateFile,
        text,
        parseOptions
      )
      setParsedTemplate(
        parseResponse.data.templateParse.result || {
          information: {
            "template-label": "",
            description: "",
            exceptions: [],
          },
          sections: [],
        }
      )
      setUserInputs((prevUserInputs) => ({
        ...prevUserInputs,
        "template-label": text,
      }))
    } catch (error) {
      console.error("Error parsing template:", error)
    }
  }

  /**
   * Handles the "Next" button click to proceed to the next step or finalize the process.
   *
   * @async
   * @function handleNext
   * @returns Promise that resolves when next step is processed
   */
  const handleNext = async (): Promise<void> => {
    const mergeOptions: TemplateOptions = {
      inMaster,
      qnrEmpty,
    }

    if (currentStep === parsedTemplate.sections.length + 2) {
      if (editor) {
        editor.commands.insertContent(templateOutput)
      }
      hideModal()
      return
    }

    const currentSection = parsedTemplate.sections[currentStep - 2]

    if (currentSection) {
      if (currentStep - 2 === parsedTemplate.sections.length - 1) {
        try {
          // Pass the structured userInputs as expected by the API
          const templateUserInputs: TemplateUserInputs =
            userInputs as unknown as TemplateUserInputs

          const mergeRes = await mergeTemplateWithUserInputs(
            templateUserInputs,
            selectedTemplateFile,
            mergeOptions
          )
          setTemplateOutput(mergeRes.data)
          setCurrentStep(parsedTemplate.sections.length + 2)
        } catch (error) {
          console.error("Error merging template:", error)
        }
      } else {
        setCurrentStep(currentStep + 1)
      }
    }
  }

  /**
   * Handles the "Previous" button click to return to the previous step.
   *
   * @function handlePrev
   */
  const handlePrev = (): void => {
    if (currentStep === 2) {
      setUserInputs({
        "template-label": "",
        inputs: [],
      })
      setCurrentStep(1)
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  /**
   * Updates the value of a user input.
   *
   * @function updateUserInputValue
   * @param label - The label of the input field
   * @param value - The value to be set for the input field
   */
  const updateUserInputValue = (label: string, value: string): void => {
    setUserInputs((prevUserInputs) => {
      const existingInputIndex = prevUserInputs.inputs.findIndex(
        (input) => input.label === label
      )

      if (existingInputIndex >= 0) {
        const updatedInputs = [...prevUserInputs.inputs]
        updatedInputs[existingInputIndex] = { label, value }
        return {
          ...prevUserInputs,
          inputs: updatedInputs,
        }
      } else {
        return {
          ...prevUserInputs,
          inputs: [...prevUserInputs.inputs, { label, value }],
        }
      }
    })
  }

  /**
   * Renders the content for the current step in the wizard.
   *
   * @function renderStepContent
   * @returns The content for the current step
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <TemplateFiles
            templateFiles={templateFiles}
            onItemClick={handleTemplateFilesItemClick}
          />
        )
      case 1:
        return (
          <TemplateList
            templates={templates}
            listingTemplatesFromFile={listingTemplatesFromFile}
            onItemClick={handleTemplateItemClick}
          />
        )
      case parsedTemplate.sections.length + 2:
        return <PreviewTemplate templateOutput={templateOutput} />
      default: {
        const currentSection = parsedTemplate.sections[currentStep - 2]
        // Safety check: if section doesn't exist, render nothing or fallback
        if (!currentSection) {
          return (
            <div className="flex flex-col items-center justify-center h-full">
              <p>No section found. Please go back and try again.</p>
            </div>
          )
        }
        return (
          <SelectedTemplateForm
            exceptions={parsedTemplate.information.exceptions}
            parsingSelectedTemplate={parsingSelectedTemplate}
            section={currentSection}
            values={userInputs.inputs.reduce((acc, input) => {
              acc[input.label] = input.value
              return acc
            }, {} as Record<string, string>)}
            onChange={updateUserInputValue}
          />
        )
      }
    }
  }

  return (
    <div className="flex flex-col mt-5">
      <div className="flex flex-col max-h-[800px] overflow-auto">
        {renderStepContent()}
      </div>
      <div className="ml-auto space-x-2 mt-5">
        {currentStep > 0 && (
          <DataQButton onClick={handlePrev} type="primary">
            Previous
          </DataQButton>
        )}
        {currentStep > 1 &&
          currentStep <= parsedTemplate.sections.length + 2 && (
            <DataQButton onClick={handleNext} type="primary">
              {currentStep === parsedTemplate.sections.length + 2
                ? `Finalise`
                : `Next`}
            </DataQButton>
          )}
        <DataQButton onClick={hideModal} type="secondary">
          Cancel
        </DataQButton>
      </div>
    </div>
  )
}

export default TemplateWizard
